import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import LinkOffRoundedIcon from "@mui/icons-material/LinkOffRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import PauseCircleOutlineRoundedIcon from "@mui/icons-material/PauseCircleOutlineRounded";
import CloudDoneRoundedIcon from "@mui/icons-material/CloudDoneRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";

function localIso(dateKey, clock) {
  const date = new Date(`${dateKey}T${clock}:00`);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
}

function formatDateTime(value) {
  if (!value) return "Nunca";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Nunca";
  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function statusPresentation(integration) {
  if (!integration?.connected) return { label: "Sin conectar", color: "default", icon: <LinkOffRoundedIcon fontSize="small" /> };
  if (integration?.status === "error") return { label: "Requiere atención", color: "error", icon: <LinkOffRoundedIcon fontSize="small" /> };
  if (integration?.paused || integration?.syncEnabled === false) return { label: "Pausado", color: "warning", icon: <PauseCircleOutlineRoundedIcon fontSize="small" /> };
  return { label: "Conectado", color: "success", icon: <CloudDoneRoundedIcon fontSize="small" /> };
}

export function OwnerGoogleCalendarDialog({ open, onClose, currentIntegration, onChanged }) {
  const [integration, setIntegration] = React.useState(currentIntegration || null);
  const [calendars, setCalendars] = React.useState([]);
  const [providerForm, setProviderForm] = React.useState({ clientId: "", clientSecret: "" });
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");

  const hydrateProviderForm = React.useCallback((nextIntegration) => {
    setProviderForm((current) => ({
      clientId: nextIntegration?.provider?.clientId || current.clientId || "",
      clientSecret: "",
    }));
  }, []);

  const load = React.useCallback(async () => {
    if (!open) return;
    setLoading(true);
    setError("");
    try {
      const response = await ownerApi.googleCalendarStatus();
      const nextIntegration = response.integration || null;
      setIntegration(nextIntegration);
      hydrateProviderForm(nextIntegration);
      if (nextIntegration?.connected && nextIntegration?.status !== "error") {
        const calendarResponse = await ownerApi.listGoogleCalendars();
        setCalendars(calendarResponse.calendars || []);
      } else {
        setCalendars([]);
      }
    } catch (err) {
      setError(formatApiError(err, "No pude leer la conexión con Google Calendar."));
    } finally {
      setLoading(false);
    }
  }, [open, hydrateProviderForm]);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    if (!open) return;
    setNotice("");
    if (currentIntegration) {
      setIntegration(currentIntegration);
      hydrateProviderForm(currentIntegration);
    }
  }, [open, currentIntegration, hydrateProviderForm]);

  const copyRedirect = async () => {
    const value = integration?.provider?.redirectUri || integration?.redirectUri || "";
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setNotice("Callback OAuth copiado.");
    } catch {
      setNotice("Copiá manualmente el callback OAuth mostrado abajo.");
    }
  };

  const saveProvider = async () => {
    setSaving(true); setError(""); setNotice("");
    try {
      const response = await ownerApi.saveGoogleCalendarProviderConfig({
        clientId: providerForm.clientId.trim(),
        clientSecret: providerForm.clientSecret.trim(),
      });
      const nextIntegration = response.integration || integration;
      setIntegration(nextIntegration);
      hydrateProviderForm(nextIntegration);
      setNotice("OAuth de Google guardado cifrado. Ya podés conectar tu cuenta.");
      await onChanged?.();
    } catch (err) {
      setError(formatApiError(err, "No pude guardar la configuración OAuth de Google."));
    } finally {
      setSaving(false);
    }
  };

  const deleteProvider = async () => {
    if (!window.confirm("¿Eliminar la configuración OAuth de Google? También se dará de baja cualquier conexión activa. Tus eventos de Google no se borran.")) return;
    setSaving(true); setError(""); setNotice("");
    try {
      const response = await ownerApi.deleteGoogleCalendarProviderConfig();
      setIntegration(response.integration || null);
      setCalendars([]);
      setProviderForm({ clientId: "", clientSecret: "" });
      setNotice("Configuración OAuth eliminada del Owner.");
      await onChanged?.();
    } catch (err) {
      setError(formatApiError(err, "No pude eliminar la configuración OAuth."));
    } finally {
      setSaving(false);
    }
  };

  const connect = async () => {
    setSaving(true); setError(""); setNotice("");
    try {
      const response = await ownerApi.startGoogleCalendarConnection();
      if (!response.authorizationUrl) throw new Error("Google no devolvió una URL de autorización.");
      window.location.assign(response.authorizationUrl);
    } catch (err) {
      setError(formatApiError(err, "No pude iniciar la conexión con Google."));
      setSaving(false);
    }
  };

  const chooseCalendar = async (calendarId) => {
    setSaving(true); setError(""); setNotice("");
    try {
      const response = await ownerApi.selectGoogleCalendar(calendarId);
      setIntegration(response.integration || integration);
      setNotice("Calendario principal del Owner actualizado.");
      await onChanged?.();
    } catch (err) {
      setError(formatApiError(err, "No pude cambiar el calendario de Google."));
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = async (key, value) => {
    const previous = integration;
    setIntegration((current) => ({ ...(current || {}), [key]: value, ...(key === "syncEnabled" ? { paused: !value } : {}) }));
    setSaving(true); setError(""); setNotice("");
    try {
      const response = await ownerApi.updateGoogleCalendarSettings({ [key]: value });
      setIntegration(response.integration || previous);
      await onChanged?.();
    } catch (err) {
      setIntegration(previous);
      setError(formatApiError(err, "No pude guardar la configuración de Google Calendar."));
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setSaving(true); setError(""); setNotice("");
    try {
      const response = await ownerApi.testGoogleCalendarConnection();
      if (response.test?.integration) setIntegration(response.test.integration);
      setNotice(`Conexión correcta${Number.isFinite(Number(response.test?.latencyMs)) ? ` · ${response.test.latencyMs} ms` : ""}.`);
      const calendarResponse = await ownerApi.listGoogleCalendars();
      setCalendars(calendarResponse.calendars || []);
      await onChanged?.();
    } catch (err) {
      setError(formatApiError(err, "La prueba de conexión con Google falló."));
      await load();
    } finally {
      setSaving(false);
    }
  };

  const syncNow = async () => {
    setSaving(true); setError(""); setNotice("");
    try {
      const response = await ownerApi.syncGoogleCalendarNow();
      if (response.sync?.integration) setIntegration(response.sync.integration);
      setNotice(`Google actualizado · ${Number(response.sync?.eventCount || 0)} eventos leídos.`);
      await onChanged?.();
    } catch (err) {
      setError(formatApiError(err, "No pude actualizar Google Calendar ahora."));
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("¿Desconectar Google Calendar? WebProfit eliminará sus tokens e intentará revocar el permiso en Google. Tus eventos NO se borran.")) return;
    setSaving(true); setError(""); setNotice("");
    try {
      const response = await ownerApi.disconnectGoogleCalendar();
      const nextIntegration = response.integration || { connected: false, status: "disconnected" };
      setIntegration(nextIntegration);
      setCalendars([]);
      setNotice(response.integration?.warning || "Cuenta Google desconectada. La configuración OAuth queda lista para volver a conectar.");
      await onChanged?.();
    } catch (err) {
      setError(formatApiError(err, "No pude desconectar Google Calendar."));
    } finally {
      setSaving(false);
    }
  };

  const connected = Boolean(integration?.connected);
  const provider = integration?.provider || {};
  const configured = Boolean(provider.configured || integration?.configured);
  const encryptionOk = provider.encryptionReady !== false;
  const status = statusPresentation(integration);
  const redirectUri = provider.redirectUri || integration?.redirectUri || "";

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 1000, pb: 1 }}>Google Calendar · ABM de conexión</DialogTitle>
      <DialogContent>
        <Stack spacing={1.35} sx={{ pt: .35 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {notice ? <Alert severity="success">{notice}</Alert> : null}
          {!encryptionOk ? <Alert severity="error">El backend no tiene una clave segura para cifrar tokens OAuth. Configurá PAYMENTS_ENCRYPTION_KEY, CREDENTIALS_ENCRYPTION_KEY o un SESSION_SECRET seguro.</Alert> : null}

          <Box sx={{ p: 1.35, border: "1px solid", borderColor: "divider", borderRadius: 2.4, bgcolor: "background.paper" }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} alignItems={{ xs: "stretch", sm: "center" }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                <CalendarMonthRoundedIcon color={connected ? "primary" : "disabled"} />
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={.7} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography sx={{ fontWeight: 1000, fontSize: ".9rem" }}>{connected ? (integration.accountEmail || "Cuenta Google") : configured ? "Google listo para autorizar" : "Google OAuth sin configurar"}</Typography>
                    <Chip size="small" color={connected ? status.color : configured ? "info" : "warning"} icon={connected ? status.icon : <LinkOffRoundedIcon fontSize="small" />} label={connected ? status.label : configured ? "Listo para conectar" : "Falta configuración"} sx={{ fontWeight: 900 }} />
                  </Stack>
                  <Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: ".71rem", mt: .2 }}>
                    {connected
                      ? `${integration.calendarSummary || "Calendario principal"}${integration.calendarTimeZone ? ` · ${integration.calendarTimeZone}` : ""}`
                      : configured
                        ? "Autorizá la misma cuenta Google que usa tu iPhone/Siri."
                        : "Cargá una credencial OAuth tipo Web application y registrá exactamente el callback mostrado abajo."}
                  </Typography>
                </Box>
              </Stack>
              {connected ? (
                <Stack direction="row" spacing={.65}>
                  <Button size="small" variant="outlined" startIcon={<FactCheckRoundedIcon />} onClick={testConnection} disabled={saving || loading}>Probar</Button>
                  <Button size="small" variant="outlined" startIcon={<SyncRoundedIcon />} onClick={syncNow} disabled={saving || loading || integration.syncEnabled === false}>Actualizar</Button>
                </Stack>
              ) : configured ? <Button variant="contained" startIcon={<LinkRoundedIcon />} onClick={connect} disabled={saving || loading || !encryptionOk}>Conectar Google</Button> : null}
            </Stack>
          </Box>

          <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2.2, p: 1.25 }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} alignItems={{ xs: "stretch", sm: "center" }} sx={{ mb: 1 }}>
              <Box>
                <Typography sx={{ fontWeight: 1000, fontSize: ".78rem" }}>CONFIGURACIÓN OAUTH</Typography>
                <Typography sx={{ color: "text.secondary", fontSize: ".67rem", fontWeight: 700 }}>Podés administrarla desde Owner. Si existen variables de entorno, siguen funcionando como fallback.</Typography>
              </Box>
              <Stack direction="row" spacing={.55}>
                <Button size="small" variant="text" component="a" href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" endIcon={<LaunchRoundedIcon />}>Google Cloud</Button>
                {provider.ownerConfigured ? <Button size="small" color="error" variant="text" startIcon={<DeleteForeverRoundedIcon />} onClick={deleteProvider} disabled={saving}>Eliminar OAuth</Button> : null}
              </Stack>
            </Stack>

            <Stack spacing={1}>
              <TextField
                label="OAuth Client ID · Web application"
                value={providerForm.clientId}
                onChange={(e) => setProviderForm((current) => ({ ...current, clientId: e.target.value }))}
                placeholder="123456789-xxxxx.apps.googleusercontent.com"
                disabled={saving}
                helperText={provider.source === "environment" ? "Actualmente viene de GOOGLE_CALENDAR_CLIENT_ID. Si guardás acá, Owner pasa a usar esta configuración como override." : "El Client ID no es secreto; se usa para iniciar OAuth."}
              />
              <TextField
                type="password"
                label={provider.ownerConfigured ? "OAuth Client Secret · dejar vacío para conservar" : "OAuth Client Secret"}
                value={providerForm.clientSecret}
                onChange={(e) => setProviderForm((current) => ({ ...current, clientSecret: e.target.value }))}
                disabled={saving}
                autoComplete="new-password"
              />
              <Box sx={{ p: .9, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.025)", borderRadius: 1.6 }}>
                <Typography sx={{ color: "text.secondary", fontSize: ".62rem", fontWeight: 900, mb: .3 }}>AUTHORIZED REDIRECT URI · DEBE COINCIDIR EXACTAMENTE</Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={.7} alignItems={{ xs: "stretch", sm: "center" }}>
                  <Typography component="code" sx={{ flex: 1, fontSize: ".7rem", overflowWrap: "anywhere" }}>{redirectUri || "—"}</Typography>
                  <Button size="small" variant="outlined" startIcon={<ContentCopyRoundedIcon />} onClick={copyRedirect} disabled={!redirectUri}>Copiar</Button>
                </Stack>
              </Box>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={.7}>
                <Button variant="outlined" startIcon={<SaveRoundedIcon />} onClick={saveProvider} disabled={saving || !encryptionOk || !providerForm.clientId.trim() || (!provider.ownerConfigured && !providerForm.clientSecret.trim())}>Guardar OAuth</Button>
                {configured && !connected ? <Button variant="contained" startIcon={<LinkRoundedIcon />} onClick={connect} disabled={saving || loading || !encryptionOk}>Conectar cuenta Google</Button> : null}
              </Stack>
            </Stack>
          </Box>

          {connected ? (
            <>
              {integration.lastError ? <Alert severity="warning">{integration.lastError}</Alert> : null}
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.15fr .85fr" }, gap: 1.2 }}>
                <Stack spacing={1.1}>
                  <Typography sx={{ fontWeight: 1000, fontSize: ".78rem" }}>CONEXIÓN Y SINCRONIZACIÓN</Typography>
                  <FormControl fullWidth disabled={loading || saving || integration.status === "error"}>
                    <InputLabel>Calendario usado por Owner</InputLabel>
                    <Select label="Calendario usado por Owner" value={integration.calendarId || ""} onChange={(event) => chooseCalendar(event.target.value)}>
                      {calendars.map((row) => <MenuItem key={row.id} value={row.id}>{row.summary}{row.primary ? " · principal" : ""}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, px: 1.15, py: .45 }}>
                    <FormControlLabel sx={{ m: 0, width: "100%", justifyContent: "space-between", flexDirection: "row-reverse" }} control={<Switch checked={integration.syncEnabled !== false} onChange={(e) => updateSetting("syncEnabled", e.target.checked)} disabled={saving} />} label={<Box><Typography sx={{ fontWeight: 900, fontSize: ".78rem" }}>Mostrar Google en Owner</Typography><Typography sx={{ color: "text.secondary", fontSize: ".68rem" }}>Pausalo sin revocar permisos.</Typography></Box>} />
                    <Divider />
                    <FormControlLabel sx={{ m: 0, width: "100%", justifyContent: "space-between", flexDirection: "row-reverse" }} control={<Switch checked={integration.showAllDayEvents !== false} onChange={(e) => updateSetting("showAllDayEvents", e.target.checked)} disabled={saving} />} label={<Box><Typography sx={{ fontWeight: 900, fontSize: ".78rem" }}>Eventos de todo el día</Typography><Typography sx={{ color: "text.secondary", fontSize: ".68rem" }}>Incluye eventos sin horario.</Typography></Box>} />
                    <Divider />
                    <FormControlLabel sx={{ m: 0, width: "100%", justifyContent: "space-between", flexDirection: "row-reverse" }} control={<Switch checked={integration.blockBusyTime !== false} onChange={(e) => updateSetting("blockBusyTime", e.target.checked)} disabled={saving} />} label={<Box><Typography sx={{ fontWeight: 900, fontSize: ".78rem" }}>Bloquear horarios ocupados</Typography><Typography sx={{ color: "text.secondary", fontSize: ".68rem" }}>Evita superponer agenda interna con eventos de Google.</Typography></Box>} />
                    <Divider />
                    <FormControl fullWidth size="small" sx={{ my: .8 }}>
                      <InputLabel>Aviso por defecto</InputLabel>
                      <Select label="Aviso por defecto" value={String(integration.defaultReminderMinutes ?? 15)} onChange={(e) => updateSetting("defaultReminderMinutes", Number(e.target.value))} disabled={saving}>
                        <MenuItem value="0">Sin aviso</MenuItem>
                        <MenuItem value="5">5 minutos antes</MenuItem>
                        <MenuItem value="10">10 minutos antes</MenuItem>
                        <MenuItem value="15">15 minutos antes</MenuItem>
                        <MenuItem value="30">30 minutos antes</MenuItem>
                        <MenuItem value="60">1 hora antes</MenuItem>
                        <MenuItem value="1440">1 día antes</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Stack>

                <Stack spacing={1.05}>
                  <Typography sx={{ fontWeight: 1000, fontSize: ".78rem" }}>DIAGNÓSTICO</Typography>
                  <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 1.15 }}>
                    <Stack spacing={.75}>
                      <Box><Typography sx={{ color: "text.secondary", fontSize: ".65rem", fontWeight: 900 }}>CUENTA</Typography><Typography sx={{ fontSize: ".76rem", fontWeight: 850, overflowWrap: "anywhere" }}>{integration.accountEmail || "—"}</Typography></Box>
                      <Box><Typography sx={{ color: "text.secondary", fontSize: ".65rem", fontWeight: 900 }}>ÚLTIMA LECTURA</Typography><Typography sx={{ fontSize: ".76rem", fontWeight: 850 }}>{formatDateTime(integration.lastSuccessfulAt)}</Typography></Box>
                      <Box><Typography sx={{ color: "text.secondary", fontSize: ".65rem", fontWeight: 900 }}>ÚLTIMA PRUEBA</Typography><Typography sx={{ fontSize: ".76rem", fontWeight: 850 }}>{formatDateTime(integration.lastCheckedAt)}</Typography></Box>
                      <Box><Typography sx={{ color: "text.secondary", fontSize: ".65rem", fontWeight: 900 }}>ORIGEN OAUTH</Typography><Typography sx={{ fontSize: ".76rem", fontWeight: 850 }}>{provider.source === "owner" ? "ABM Owner" : provider.source === "environment" ? "Variables de entorno" : "—"}</Typography></Box>
                    </Stack>
                  </Box>
                </Stack>
              </Box>
              <Divider />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={.7}>
                <Button color="error" variant="outlined" startIcon={<LinkOffRoundedIcon />} onClick={disconnect} disabled={saving}>Desconectar y revocar</Button>
                <Button variant="text" startIcon={<LinkRoundedIcon />} onClick={connect} disabled={saving || !configured}>Cambiar / reconectar cuenta</Button>
              </Stack>
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.3 }}><Button onClick={onClose} disabled={saving}>Cerrar</Button></DialogActions>
    </Dialog>
  );
}

export function OwnerGoogleEventDialog({ open, onClose, selectedDate, onSaved }) {
  const [form, setForm] = React.useState({ title: "", date: selectedDate || "", allDay: false, startTime: "09:00", endTime: "10:00", reminderMinutes: 15, location: "", description: "" });
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    setForm({ title: "", date: selectedDate || "", allDay: false, startTime: "09:00", endTime: "10:00", reminderMinutes: 15, location: "", description: "" });
    setError("");
  }, [open, selectedDate]);

  const submit = async () => {
    setSaving(true); setError("");
    try {
      const body = {
        title: form.title,
        date: form.date,
        allDay: form.allDay,
        location: form.location,
        description: form.description,
        reminderMinutes: Number(form.reminderMinutes || 0),
      };
      if (!form.allDay) {
        body.start = localIso(form.date, form.startTime);
        body.end = localIso(form.date, form.endTime);
      }
      await ownerApi.createGoogleCalendarEvent(body);
      await onSaved?.();
      onClose?.();
    } catch (err) {
      setError(formatApiError(err, "No pude crear el evento en Google Calendar."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 1000 }}>Nuevo evento en Google</DialogTitle>
      <DialogContent>
        <Stack spacing={1.25} sx={{ pt: .5 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField autoFocus label="Evento" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ej.: Reunión, turno, llamada" />
          <TextField type="date" label="Fecha" InputLabelProps={{ shrink: true }} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          <FormControlLabel control={<Checkbox checked={form.allDay} onChange={(e) => setForm({ ...form, allDay: e.target.checked })} />} label="Todo el día" />
          {!form.allDay ? (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
              <TextField fullWidth type="time" label="Desde" InputLabelProps={{ shrink: true }} value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
              <TextField fullWidth type="time" label="Hasta" InputLabelProps={{ shrink: true }} value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} />
            </Stack>
          ) : null}
          <FormControl fullWidth>
            <InputLabel>Notificación</InputLabel>
            <Select label="Notificación" value={String(form.reminderMinutes)} onChange={(e) => setForm({ ...form, reminderMinutes: Number(e.target.value) })}>
              <MenuItem value="0">Sin aviso</MenuItem>
              <MenuItem value="5">5 minutos antes</MenuItem>
              <MenuItem value="10">10 minutos antes</MenuItem>
              <MenuItem value="15">15 minutos antes</MenuItem>
              <MenuItem value="30">30 minutos antes</MenuItem>
              <MenuItem value="60">1 hora antes</MenuItem>
              <MenuItem value="1440">1 día antes</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Lugar (opcional)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          <TextField multiline minRows={2} label="Descripción (opcional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: ".69rem" }}>Este evento se crea directamente en Google. Si ese calendario está sincronizado con tu iPhone, también lo vas a ver en Calendar/Siri.</Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.3 }}>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={saving || !form.title.trim() || !form.date}>{saving ? "Creando…" : "Crear en Google"}</Button>
      </DialogActions>
    </Dialog>
  );
}
